import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Button as MantineButton,
  Card,
  Divider,
  Group,
  lighten,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Typography,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import Button from 'components/button/button';
import Fa from 'components/fa';
import NoAccessButton from 'components/worksheet/no_access_button';

const callColors = [
  {foreground: '#2F8F3E', background: '#D9F0DC'},
  {foreground: '#5B53D6', background: '#E6E6FA'},
  {foreground: '#C9791B', background: '#FBE6CF'},
];

const visibleCallCount = 5;
const callListHeight = 328;
const tenMinutesInMilliseconds = 10 * 60 * 1000;
const oneHourInMilliseconds = 60 * 60 * 1000;
const expirationRefreshRetryDelay = 10 * 1000;
const maxTimerDelay = 24 * 60 * 60 * 1000;

const formatTime = date => date.toLocaleTimeString(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const calendarDayValue = date => Date.UTC(
  date.getFullYear(),
  date.getMonth(),
  date.getDate(),
);

const formatAvailabilityLabel = (availableAt, currentTime) => {
  const currentDate = new Date(currentTime);
  const daysUntilAvailable = (
    calendarDayValue(availableAt) - calendarDayValue(currentDate)
  ) / (24 * 60 * 60 * 1000);

  if (daysUntilAvailable === 0) {
    return `Available at ${formatTime(availableAt)}`;
  }
  if (daysUntilAvailable === 1) {
    return 'Available tomorrow';
  }
  if (daysUntilAvailable <= 6) {
    const weekday = availableAt.toLocaleDateString('en-US', {weekday: 'long'});
    return `Available ${weekday}`;
  }

  const date = availableAt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `Available ${date}`;
};

const formatCallDate = value => {
  const date = new Date(value);
  const month = date.toLocaleDateString('en-US', {month: 'short'}).toUpperCase();
  const day = date.toLocaleDateString('en-US', {day: 'numeric'});
  const weekday = date.toLocaleDateString('en-US', {weekday: 'short'});
  const fullWeekday = date.toLocaleDateString('en-US', {weekday: 'long'});
  const time = formatTime(date);

  return {
    month,
    day,
    subtitle: `${weekday} · ${time}`,
    fullSubtitle: `${fullWeekday}, ${time} (your time)`,
  };
};

const GetReadyContent = styled(Typography)`
  ul li {
    list-style-type: disc;
  }
  ol li {
    list-style-type: decimal;
  }
`;

const BookTextIcon = () => (
  <svg
    fill='none'
    height='1.2em'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth={2}
    viewBox='0 0 24 24'
    width='1.2em'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20' />
    <path d='M8 11h8' />
    <path d='M8 7h6' />
  </svg>
);

const GetReadyButton = ({
  accentColor,
  label = 'Get ready',
  size = 'xs',
  onClick,
}) => (
  <MantineButton
    color={accentColor}
    leftSection={<BookTextIcon />}
    radius='xl'
    size={size}
    styles={{
      root: {
        background: 'white',
        borderColor: lighten(accentColor, 0.7),
        color: accentColor,
        flexShrink: 0,
      },
      section: {marginInlineEnd: 6},
    }}
    variant='outline'
    onClick={onClick}
  >
    {label}
  </MantineButton>
);

const GetReadyModal = ({accentColor, call, onClose}) => {
  const formattedDate = call && formatCallDate(call.date);

  return (
    <Modal
      centered
      opened={Boolean(call)}
      radius='md'
      size='lg'
      styles={{
        header: {
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          paddingInlineEnd: 'var(--mantine-spacing-lg)',
        },
        body: {paddingTop: 'var(--mantine-spacing-md)'},
      }}
      title={
        call &&
          <Stack gap={2}>
            <Text c={accentColor} fw={700} size='xs'>GET READY</Text>
            <Title order={3} size='lg'>{call.title}</Title>
            <Text c='dimmed' size='sm'>{formattedDate.fullSubtitle}</Text>
          </Stack>
      }
      onClose={onClose}
    >
      {
        call &&
          <GetReadyContent>
            <div dangerouslySetInnerHTML={{__html: call.getReady}} />
          </GetReadyContent>
      }
    </Modal>
  );
};

const ConversationCallsList = ({accentColor, conversationCalls, onOpenGetReady}) => (
  <Stack gap={0}>
    {
      conversationCalls.map((call, index) => {
        const callColor = callColors[index % callColors.length];
        const formattedDate = formatCallDate(call.date);

        return (
            <Box key={call.id || `${call.date}-${call.title}`}>
            <Group gap='md' py={8} wrap='nowrap'>
              <Stack
                align='center'
                bg={callColor.background}
                gap={2}
                h={48}
                justify='center'
                w={42}
                style={{borderRadius: 'var(--mantine-radius-md)', flexShrink: 0}}
              >
                <Text c={callColor.foreground} fw={700} fz={10} lh={1}>
                  {formattedDate.month}
                </Text>
                <Text c={callColor.foreground} fw={700} size='sm' lh={1}>
                  {formattedDate.day}
                </Text>
              </Stack>
              <Stack gap={0} style={{flex: 1}}>
                <Text fw={700} size='sm'>{call.title}</Text>
                <Text c='dimmed' size='xs'>{formattedDate.subtitle}</Text>
              </Stack>
              {
                call.getReady && onOpenGetReady &&
                  <GetReadyButton
                    accentColor={accentColor}
                    onClick={() => onOpenGetReady(call)}
                  />
              }
            </Group>
            {index < conversationCalls.length - 1 && <Divider />}
          </Box>
        );
      })
    }
  </Stack>
);

export const ConversationCallsCard = ({
  accentColor = '#2E8629',
  conversationCalls = [],
  currentTime = Date.now(),
  getReadyCall,
  isSmallScreen = false,
  joinBlocked = false,
  joining = false,
  showAll = false,
  onCloseGetReady,
  onJoin,
  onOpenGetReady,
  onShowAll,
  onShowLess,
}) => {
  if (conversationCalls.length === 0) {
    return null;
  }

  const nextCall = conversationCalls[0];
  const nextCallDate = nextCall && formatCallDate(nextCall.date);
  const nextCallStartTime = nextCall && new Date(nextCall.date).getTime();
  const linkAvailableTime = nextCall &&
    new Date(nextCallStartTime - tenMinutesInMilliseconds);
  const hasAccess = nextCall.accessStatus?.hasAccess !== false;
  const joinCallIsTooEarly =
    nextCallStartTime - currentTime > tenMinutesInMilliseconds;
  const joinCallDisabled =
    !nextCall.id || joinCallIsTooEarly || joining || joinBlocked || !onJoin;
  const joinCallLabel = joining
    ? 'Joining...'
    : joinCallIsTooEarly
      ? formatAvailabilityLabel(linkAvailableTime, currentTime)
      : 'Join Call';
  const nextCallIsOngoing =
    nextCallStartTime < currentTime;
  const upcomingCalls = conversationCalls.slice(1);
  const visibleCalls = showAll
    ? upcomingCalls
    : upcomingCalls.slice(0, visibleCallCount);
  const callList = (
    <ConversationCallsList
      accentColor={accentColor}
      conversationCalls={visibleCalls}
      onOpenGetReady={onOpenGetReady}
    />
  );
  const showExpandedCalls = showAll && isSmallScreen;
  const showScrollableCalls = showAll && !isSmallScreen;
  const showToggle = upcomingCalls.length > visibleCallCount &&
    (!showAll || isSmallScreen);

  return (
    <Card withBorder radius='md' p={0}>
      <SimpleGrid cols={{base: 1, sm: 2}} spacing={0}>
        <Stack bg={lighten(accentColor, 0.9)} p='xl' mih={450}>
          <Group gap='xs'>
            <Box bg={accentColor} w={8} h={8} style={{borderRadius: '50%'}} />
            <Text c={accentColor} fw={700} size='xs'>
              {nextCallIsOngoing ? 'ONGOING CONVERSATION CALL' : 'NEXT CONVERSATION CALL'}
            </Text>
          </Group>
          <Title order={2} size='xl'>{nextCall?.title}</Title>
          <Text c='dimmed' size='sm'>{nextCallDate?.fullSubtitle}</Text>
          {
            nextCall.getReady && onOpenGetReady &&
              <Group>
                <GetReadyButton
                  accentColor={accentColor}
                  label='Get ready: topics & vocab'
                  size='sm'
                  onClick={() => onOpenGetReady(nextCall)}
                />
              </Group>
          }
          <Group mt='auto'>
            {
              hasAccess ? (
                <Button
                  background={accentColor}
                  backgroundOnActive={accentColor}
                  backgroundOnHover={accentColor}
                  disabled={joinCallDisabled}
                  onClick={
                    joinCallDisabled ? undefined : () => onJoin(nextCall)
                  }
                  style={
                    joinCallDisabled
                      ? {background: 'var(--mantine-color-gray-6)'}
                      : undefined
                  }
                >
                  {joinCallLabel}
                </Button>
              ) : (
                <NoAccessButton
                  margin='0'
                  reason={nextCall.accessStatus.reason}
                />
              )
            }
          </Group>
        </Stack>
        <Stack
          gap='xs'
          h={isSmallScreen ? 'auto' : 450}
          mih={450}
          p='xl'
          style={{borderLeft: '1px solid var(--mantine-color-gray-3)'}}
        >
          <Text fw={700}>Upcoming calls</Text>
          {
            showScrollableCalls ? (
              <ScrollArea
                h={callListHeight}
                offsetScrollbars
                scrollbarSize={8}
                style={{flexShrink: 0}}
                type='always'
              >
                {callList}
              </ScrollArea>
            ) : (
              <Box
                h={showExpandedCalls ? 'auto' : callListHeight}
                style={{
                  overflow: showExpandedCalls ? 'visible' : 'hidden',
                  flexShrink: 0,
                }}
              >
                {callList}
              </Box>
            )
          }
          {
            showToggle &&
              <Group justify='flex-end' mt='auto'>
                <Button
                  onClick={showAll ? onShowLess : onShowAll}
                  background='transparent'
                  backgroundOnActive='transparent'
                  backgroundOnHover='transparent'
                  color='var(--mantine-color-dark-9)'
                  fontSize='12px'
                  height='auto'
                  iconMarginRight={0}
                  padding={0}
                  scaleOnActive={1}
                >
                  {showAll ? 'Show less' : 'Show all'}
                  <Fa
                    i={showAll ? 'arrow-up' : 'arrow-down'}
                    style={{marginLeft: '6px'}}
                  />
                </Button>
              </Group>
          }
        </Stack>
      </SimpleGrid>
      <GetReadyModal
        accentColor={accentColor}
        call={getReadyCall}
        onClose={onCloseGetReady}
      />
    </Card>
  );
};

export const ConversationCallsCardWrapper = props => {
  const {conversationCalls, onConversationCallsRefresh} = props;
  const isSmallScreen = useMediaQuery('(max-width: 47.99em)');
  const [showAll, setShowAll] = useState(false);
  const [getReadyCall, setGetReadyCall] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const nextCallStartTime = conversationCalls?.[0]?.date &&
    new Date(conversationCalls[0].date).getTime();
  const linkAvailableTime =
    nextCallStartTime - tenMinutesInMilliseconds;
  const callExpirationTime =
    nextCallStartTime + oneHourInMilliseconds;
  let callExpirationRefreshTime = callExpirationTime;
  if (callExpirationTime <= currentTime && onConversationCallsRefresh) {
    callExpirationRefreshTime = currentTime + expirationRefreshRetryDelay;
  }

  useEffect(() => {
    const nextMidnight = new Date(currentTime);
    nextMidnight.setHours(24, 0, 0, 0);
    const nextTransition = [
      linkAvailableTime,
      nextCallStartTime,
      callExpirationRefreshTime,
      nextCallStartTime > currentTime ? nextMidnight.getTime() : undefined,
    ]
      .filter(time => time > currentTime)
      .sort((first, second) => first - second)[0];

    if (!nextTransition) {
      return undefined;
    }

    const delay = Math.min(
      nextTransition - currentTime + 100,
      maxTimerDelay,
    );
    const timer = window.setTimeout(() => {
      const updatedCurrentTime = Date.now();
      setCurrentTime(updatedCurrentTime);
      if (
        [linkAvailableTime, callExpirationRefreshTime].includes(nextTransition) &&
        updatedCurrentTime >= nextTransition
      ) {
        onConversationCallsRefresh?.();
      }
    }, delay);

    return () => window.clearTimeout(timer);
  }, [
    callExpirationRefreshTime,
    currentTime,
    linkAvailableTime,
    nextCallStartTime,
    onConversationCallsRefresh,
  ]);

  return (
    <ConversationCallsCard
      {...props}
      currentTime={currentTime}
      getReadyCall={getReadyCall}
      isSmallScreen={isSmallScreen}
      showAll={showAll}
      onCloseGetReady={() => setGetReadyCall(null)}
      onOpenGetReady={setGetReadyCall}
      onShowAll={() => setShowAll(true)}
      onShowLess={() => setShowAll(false)}
    />
  );
};

export default ConversationCallsCardWrapper;
